// Inline thread notices: turn-failure strip (with one-click retry) and the
// socket-health pill. Both are self-contained — they read the store and
// re-send through the same choke point every send path uses — so ThreadBody
// just drops them in.

import { type FC } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Loader, Text } from '@kula-ai/components';
import { AlertCircle, RefreshCw01, XClose } from '@kula-ai/icons-core';
import { TURN_INCOMPLETE } from '../../../constants';
import { useChatSubmit } from '../../../hooks';
import { useAgentForgeStore } from '../../../stores';

// TurnErrorStrip renders when the active session's last turn failed (send
// POST failed or the stream errored — both set tab status 'error'). Before
// this, that state rendered NOTHING: the user's bubble sat unanswered with
// no explanation and no way to retry short of retyping the message.
export const TurnErrorStrip: FC = () => {
  const { send, isSending } = useChatSubmit();
  const activeSessionId = useAgentForgeStore((s) => s.activeSessionId);
  const tabStatus = useAgentForgeStore(
    (s) => s.openTabs.find((t) => t.sessionId === s.activeSessionId)?.status
  );
  const lastUserText = useAgentForgeStore((s) => {
    const sid = s.activeSessionId;
    if (!sid) return '';
    const msgs = s.messagesBySession[sid] ?? [];
    const lastUser = [...msgs].reverse().find((m) => m.role === 'user');
    return lastUser?.content ?? '';
  });

  if (tabStatus !== 'error' || !activeSessionId) return null;

  const retry = () => {
    if (isSending || !lastUserText) return;
    // silent: the user's bubble is already on screen — don't duplicate it.
    void send(lastUserText, undefined, { silent: true });
  };

  return (
    <Flex
      align="center"
      gap="8px"
      px="12px"
      py="8px"
      borderRadius="8px"
      bg="error-25"
      border="1px solid"
      borderColor="error-100"
      data-test-id="agentforge-turn-error-strip"
    >
      <Box color="error-500" display="inline-flex" flexShrink={0}>
        <AlertCircle size={14} color="currentColor" />
      </Box>
      <Text variant="small" as="span" className="text-error-700">
        {TURN_INCOMPLETE}
      </Text>
      {lastUserText && (
        <Box
          as="button"
          type="button"
          onClick={retry}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-error-700 hover:underline cursor-pointer"
          data-test-id="agentforge-turn-error-retry"
        >
          <RefreshCw01 size={12} color="currentColor" />
          Retry
        </Box>
      )}
    </Flex>
  );
};

// ConnectionPill: the socket is how replies arrive — when it's down the chat
// must say so instead of silently freezing. Shown only while the WS layer is
// actively reconnecting (transient blips under a second don't flash it
// because reconnecting flips on at the first retry, not the drop itself).
export const ConnectionPill: FC = () => {
  const reconnecting = useAgentForgeStore((s) => s.wsReconnecting);
  if (!reconnecting) return null;
  return (
    <Flex
      align="center"
      gap="6px"
      px="10px"
      py="4px"
      borderRadius="full"
      bg="secondary-50"
      border="1px solid"
      borderColor="border-color"
      w="fit-content"
      data-test-id="agentforge-connection-pill"
    >
      <Loader size={16} />
      <Text variant="small" as="span" className="text-secondary-600">
        Reconnecting… replies may take a moment
      </Text>
    </Flex>
  );
};

// QueuedMessageStrip: the follow-up the user typed mid-turn, parked until
// the running reply finishes. Cancellable — a queued send firing after the
// user changed their mind would be worse than no queue at all.
export const QueuedMessageStrip: FC = () => {
  const activeSessionId = useAgentForgeStore((s) => s.activeSessionId);
  const queued = useAgentForgeStore((s) =>
    s.activeSessionId ? s.queuedBySession[s.activeSessionId] : undefined
  );
  const clearQueued = useAgentForgeStore((s) => s.clearQueuedMessage);
  if (!queued || !activeSessionId) return null;
  return (
    <Flex
      align="center"
      gap="8px"
      px="12px"
      py="8px"
      borderRadius="8px"
      bg="secondary-25"
      border="1px dashed"
      borderColor="secondary-300"
      data-test-id="agentforge-queued-strip"
    >
      <Text variant="small" as="span" className="text-secondary-600 truncate">
        Queued — sends when this reply finishes: “{queued}”
      </Text>
      <Box
        as="button"
        type="button"
        aria-label="Cancel queued message"
        onClick={() => clearQueued(activeSessionId)}
        className="inline-flex text-secondary-500 hover:text-secondary-700 cursor-pointer flex-shrink-0"
        data-test-id="agentforge-queued-cancel"
      >
        <XClose size={12} color="currentColor" />
      </Box>
    </Flex>
  );
};

// UploadProgressStrip: attachments upload inside the send POST — without
// this, a 10 MB resume upload was invisible until the turn started.
export const UploadProgressStrip: FC = () => {
  const progress = useAgentForgeStore((s) => s.uploadProgress);
  if (progress === null || progress >= 100) return null;
  return (
    <Flex
      align="center"
      gap="8px"
      px="10px"
      py="4px"
      borderRadius="full"
      bg="secondary-50"
      border="1px solid"
      borderColor="border-color"
      w="fit-content"
      data-test-id="agentforge-upload-progress"
    >
      <Loader size={16} />
      <Text variant="small" as="span" className="text-secondary-600">
        Uploading attachments… {progress}%
      </Text>
    </Flex>
  );
};
